import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../modules/auth/store';
import { purgeAmsClientState } from '@/modules/sso/utils/ssoTelemetry';

import { authRoutes } from '../modules/auth/routes';
import { companyRoutes } from '../modules/company/routes';
import { errorRoutes } from '../router/errorRoutes';
import { groupRoutes } from '../modules/group/routes';
import { resourcesRoutes } from '../modules/resources/routes';
import { licenseRoutes } from '../modules/license/routes';
import { permissionRoutes } from '../modules/permission/routes';
import { selectionRoutes } from '../router/selectionRoutes';
import { userRoutes } from '../modules/user/routes';
// PHA routes
import { phaRoutes } from '../product/pha/routes';
//AMS routes
import { amsRoutes } from '../product/ams/routes';

import { labelRenameRoutes } from '../modules/label-rename/routes';
import App from '@/App.vue';
import { ssoRoutes } from '@/modules/sso/routes';

const routes = [
    {
        path: '/',
        name: 'app',
        component: App,
        children: [
            // This is the default landing page for all the users who dont have access to dashboards or so on
            // {
            //     path: '',
            //     name: 'Landing',
            //     exact: true,
            //     component: () => import('@/modules/landing/views/Landing.vue'),
            //     meta: {
            //         breadcrumb: [{ parent: 'Favorites', label: { label: 'Dashboard', routerLink: '/' } }],
            //     },
            // },
            ...companyRoutes,
            {
                path: 'group',
                children: groupRoutes,
            },
            {
                path: 'resources',
                children: resourcesRoutes,
            },
            {
                path: 'license',
                children: licenseRoutes,
            },
            {
                path: 'permission',
                children: permissionRoutes,
            },
            {
                path: 'user',
                children: userRoutes,
            },
            {
                path: 'label-rename',
                children: labelRenameRoutes,
            },
            ...phaRoutes,
            ...amsRoutes,
        ],
    },
    ...selectionRoutes,
    {
        path: '/logout',
        name: 'Logout',
        component: () => import('@/modules/auth/views/Login.vue'),
        beforeEnter: async () => {
            const authStore = useAuthStore();
            await authStore.logout(); // Trigger the logout action from the store
        },
    },
    ...authRoutes,
    ...ssoRoutes,
    ...errorRoutes,
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { left: 0, top: 0 };
    },
});

// route guards for checking conditions before routing users to certain routes
router.beforeEach((to, from, next) => {

    const auth = useAuthStore();
    const checkToken = auth.token;
    const companyId = auth.getLoggedUserCompanyId;
    const product = auth.selectedProduct;

    if (to.name === 'AuthCallback') {
        next();
        return;
    }

    // Unauthenticated entry / Login: reset stores then wipe origin storage.
    if (!checkToken && (to.name === 'Login' || to.name === 'ForgotPassword' || to.name === 'SetPassword')) {
        purgeAmsClientState(auth);
    }

    // Check if the user has a token but is not valid.
    if (!checkToken && to.name !== 'Login' && to.name !== 'ForgotPassword' && to.name !== 'SetPassword') {
        purgeAmsClientState(auth);
        return next({ name: 'Login' });
    }

    if (checkToken && product && to.name !== 'ProductSelect' && !companyId && to.name !== 'CompanySelect' && to.name !== 'Logout' && to.name !== 'Login') {
        // Users without the company id (aka god) must choose a company.
        return next({ name: 'CompanySelect' });
    }

    if (to.path === '/' && to.name === 'app' && product === 'AMS') {
        return next({ name: 'Home' });
    }

    // Permission-gated routes: block direct URL navigation when the user lacks
    // the meta.permissions / meta.godOnly required to view the page. God users
    // bypass; everyone else must hold every permission listed.
    if (checkToken && !auth.isGodUser) {
        if (to.meta?.godOnly) {
            return next({ name: 'Access' });
        }
        const required = to.meta?.permissions;
        if (Array.isArray(required) && required.length > 0) {
            const userPermissions = auth.getUserPermissions ?? [];
            const hasAll = required.every((p) => userPermissions.includes(p));
            if (!hasAll) {
                return next({ name: 'Access' });
            }
        }
    }

    return next();
});

export default router;
